import { skipToken } from '@reduxjs/toolkit/query/react'
import { useFetchInfluencerRelatedBrandsQuery } from '@/api/influencer-api'
import { useFetchKolsRelatedKolsQuery } from '@/api/k6-kols-api'
import { useFetchUserStatusQuery } from '@/api/user-api'
import { useFetchRelatedBrandsQuery } from '@/api/workspace-api/kol-api'
import { Duration } from '@/components/common/duration-select'
import useKolInfo from '@/components/kol/hooks/use-kol-info'
import { mockMentionedKolList } from '@/constants/mock-data/mentioned-kols'
import useIsPublicDetailPage from '@/hooks/use-is-public-detail-page'
import { usePermissions } from '@/hooks/use-permissions'
import { RelatedKolType } from '@/store/kol-detail'
import { RelatedKol } from '@/types/api/kols'
import { ListResource } from '@/types/resource-type'
import UseQueryResult from '@/types/rtk-query'
import { RelatedKolList } from '@/types/schema/kol-detail-api/related-kol'
import { PlatformShortcode } from '@/utils/convert-platform'

type UseMentionedKols = (params: {
  kolId: string
  kolDcIdHashed?: string
  platform: PlatformShortcode
  duration: Duration
  page?: number
  perPage?: number
}) => UseQueryResult<ListResource<RelatedKol>>

const useMentionedKols: UseMentionedKols = ({
  kolId,
  kolDcIdHashed,
  platform,
  duration,
  page = 1,
  perPage = 6,
}) => {
  const { isLocked } = useKolInfo(kolId, kolDcIdHashed)
  const isPublicDetailPage = useIsPublicDetailPage()
  const { withKolPermission } = usePermissions()
  const { data: userStatus } = useFetchUserStatusQuery(
    withKolPermission ? skipToken : undefined,
  )

  const mentionedKolsForPublic = useFetchKolsRelatedKolsQuery(
    isPublicDetailPage
      ? { kolId, platform, page, per_page: perPage, kol_type: 'brand' }
      : skipToken,
  )

  const mentionedKolsByAd = useFetchRelatedBrandsQuery(
    !withKolPermission && !isPublicDetailPage && userStatus?.currentWorkspaceId
      ? {
          workspaceId: userStatus.currentWorkspaceId,
          radar_uuid: kolId,
          dc_id_hashed: kolDcIdHashed,
          page,
          scope: duration,
          perPage,
          platform,
          kolType: RelatedKolType.Person,
        }
      : skipToken,
  )

  const mentionedKolsByKol = useFetchInfluencerRelatedBrandsQuery(
    withKolPermission && !isPublicDetailPage
      ? {
          radar_uuid: kolId,
          page,
          perPage,
          scope: duration,
          platform,
          kolType: RelatedKolType.Person,
        }
      : skipToken,
  )

  const mentionedKols = ((): UseQueryResult<ListResource<RelatedKol>> => {
    if (isPublicDetailPage) {
      /**
       * @todo Remove this function when other two rtk query api convert to Zod
       */
      const convertRelatedKolListToListResource = (
        list: RelatedKolList,
      ): ListResource<RelatedKol> => {
        return {
          ...list,
          data: list.data.map((relatedKol): RelatedKol => {
            return {
              ...relatedKol,
              kolType:
                relatedKol.kolType === 'brand'
                  ? RelatedKolType.Brand
                  : RelatedKolType.Person,
              mentionCount: relatedKol.mention_count,
              platformName: relatedKol.platform_name,
              etlAt: relatedKol.etl_at,
            }
          }),
          perPage: list?.per_page,
          totalPage: list?.total_page,
        }
      }

      return {
        ...mentionedKolsForPublic,
        currentData: mentionedKolsForPublic.currentData
          ? convertRelatedKolListToListResource(
              mentionedKolsForPublic.currentData,
            )
          : undefined,
        data: mentionedKolsForPublic.data
          ? convertRelatedKolListToListResource(mentionedKolsForPublic.data)
          : undefined,
      }
    }
    return !withKolPermission ? mentionedKolsByAd : mentionedKolsByKol
  })()

  const needMockData = isLocked || isPublicDetailPage

  return needMockData
    ? {
        ...mentionedKols,
        data: mentionedKols.data
          ? {
              ...((): ListResource<RelatedKol> => {
                const mentionedKolsWithMockData = (
                  mentionedKols.data.data.length > 0
                    ? [mentionedKols.data.data[0], ...mockMentionedKolList]
                    : []
                ).slice(0, perPage)

                return {
                  ...mentionedKols.data,
                  data: mentionedKolsWithMockData,
                  total: mentionedKolsWithMockData.length,
                  totalPage: 1,
                }
              })(),
            }
          : undefined,
      }
    : mentionedKols
}

export default useMentionedKols
